import { forwardRef, Ref, useEffect, useState } from 'react';

import { faTimes } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { FieldError } from 'react-hook-form';
import styled from 'styled-components';

import { usePrevious } from 'utils/reactHooks';

import { FormHelperText } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import { FontAwesomeSvgIcon } from 'components/shared';

const SelectStyles = styled.div`
  .clear-btn {
    margin-left: 10px;

    svg {
      font-size: 13px;
    }
  }
`;

export type CustomSelectProps<T> = {
  label?: string;
  className?: string;
  options: {
    value: T;
    label: string;
    style?: Record<string, any> | undefined;
  }[];
  disabled?: boolean;
  onSelected: (option) => void;
  hasClearButton?: boolean;
  valueProp?: T;
  fullWidth?: boolean;
  error?: FieldError;
  required?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  onBlur?: () => void;
};

const CustomSelect = (
  props: CustomSelectProps<string | number>,
  ref: Ref<HTMLDivElement> | undefined
) => {
  const {
    label,
    className,
    options = [],
    disabled = false,
    onSelected,
    hasClearButton,
    valueProp,
    fullWidth,
    error,
    required,
    variant = 'standard',
    onBlur
  } = props;

  const [value, setValue] = useState<string | number>(valueProp ?? '');
  const [_options, setOptions] = useState(options);

  const prevValue = usePrevious(value);
  useEffect(() => {
    if (prevValue !== undefined && prevValue !== value) {
      onSelected(value);
    }
  }, [value, prevValue, onSelected]);

  const prevValueProp = usePrevious(valueProp);
  useEffect(() => {
    if (
      (typeof valueProp === 'string' || typeof valueProp === 'number') &&
      valueProp !== prevValueProp
    ) {
      setValue(valueProp);
    }
  }, [valueProp, prevValueProp]);

  useEffect(() => {
    setOptions(options);

    if (options.length === 1) {
      setValue(options[0].value);
    }
  }, [options]);

  return (
    <SelectStyles className={classNames(className, 'd-flex', 'v-bottom')}>
      <FormControl
        size="small"
        variant={variant}
        fullWidth
        error={!!error?.type}
      >
        {label && (
          <InputLabel htmlFor={label}>
            {label}
            {required && ' *'}
          </InputLabel>
        )}
        <Select
          fullWidth={fullWidth}
          label={label}
          value={value}
          variant={variant}
          size="small"
          onChange={(e) => setValue(e.target.value)}
          disabled={!_options.length || disabled}
          ref={ref}
          error={!!error?.type}
          onBlur={onBlur}
          inputProps={{
            required
          }}
        >
          {_options.map(({ value, label, style }) => (
            <MenuItem key={value} value={value} style={style}>
              {label}
            </MenuItem>
          ))}
        </Select>
        {error && (
          <FormHelperText error={!!error?.type}>
            {error?.message}
          </FormHelperText>
        )}
      </FormControl>
      {hasClearButton && (
        <IconButton
          color="primary"
          onClick={() => setValue('')}
          size="small"
          className="clear-btn"
          disabled={!_options.length || !value}
        >
          <FontAwesomeSvgIcon icon={faTimes} fontSize="small" />
        </IconButton>
      )}
    </SelectStyles>
  );
};

export default forwardRef(CustomSelect);
